<template>
    <div>
        <v-server-table :name="tableName" :ref="tableName" url="/observations/list" :columns="columns" :options="options">

            <div slot="afterFilterWrapper" class="ml-md-2 ml-0 mt-md-0 mt-2 mb-md-0 mb-3 float-md-left">
                <div class="clearfix d-md-none d-block"></div>

                <div class="float-md-left mr-1" v-show="!dashboardFilters">
                    <div class="form-group">
                        <select v-model="inspection_form_id" class="form-control mr-2">
                            <option :value="null">-- Select An Inspection Form --</option>
                            <option v-for="form in forms" v-bind:value="form.inspection_form_id">
                                {{ form.form_name }}
                            </option>
                        </select>
                    </div>
                </div>

                <div v-if="allowFilterSaving" class="float-left mr-1">
                    <user-filters ref="obsSavedFilters" assetType="Observation" :filterKey="filterKey" @saving="setUserFilterData" @show="setUserFilterData"></user-filters>
                </div>

                <div class="float-md-left">
                    <div class="float-left">
                        <table-filters-2  ref="obsFilters"
                            filterUrl="/observations/filters"
                            v-bind:filters="filters"
                            :emptyFilters="filterDefs"
                            :filterKey="filterKey"
                            @filtersChanged="refreshTableData"
                            @filtersCleared="filtersCleared"
                            v-show="!dashboardFilters">
                            <template slot-scope="slotProps">
                                <div class="d-inline-block">
                                    <multi-select v-model="slotProps.filters['jobsite.DivisionId']" :btnLabel="(values) => ('Divisions ('+values.length+')')" :selectOptions="slotProps.filterData.divisions" :options="{multi: true}"></multi-select>
                                </div>
                                <div class="d-inline-block">
                                    <multi-select v-model="slotProps.filters['corrective_actions.status_id']" :btnLabel="(values) => ('Statuses ('+values.length+')')" :selectOptions="slotProps.filterData.statuses" :options="{multi: true}"></multi-select>
                                </div>
                                <div class="d-inline-block">
                                    <multi-select v-model="slotProps.filters['inspection_observations.compliance_status_id']" :btnLabel="(values) => ('Compliance ('+values.length+')')" :selectOptions="slotProps.filterData.complianceStatuses" :options="{multi: true}"></multi-select>
                                </div>
                                <div class="d-inline-block">
                                    <date-range-selector buttonTitle="Observation Date" v-model="slotProps.filters['inspection_observations.observation_date']" />
                                </div>
                                <div class="d-inline-block">
                                    <multi-select v-model="slotProps.filters['inspectionelement.InspectionSectionId']" :btnLabel="(values) => ('Sections ('+values.length+')')" :selectOptions="slotProps.filterData.sections" :options="{multi: true}"></multi-select>
                                </div>
                                <div class="d-inline-block">
                                    <multi-select v-model="slotProps.filters['inspectionelement.InspectionElementId']" :btnLabel="(values) => ('Elements ('+values.length+')')" :selectOptions="slotProps.filterData.elements" :options="{multi: true, groups: true}"></multi-select>
                                </div>
                                <div class="mt-2">
                                    <div class="d-inline-block" style="width: 250px;">
                                        <subcontractor-select :selectedValues="slotProps.filters['inspection_observations.subcontractor_id']" :forFilters="true" v-on:vselected="((data) => { slotProps.filters['inspection_observations.subcontractor_id'] = data || {};  })"></subcontractor-select>
                                    </div>
                                    <div class="d-inline-block" style="width: 250px;">
                                        <jobsite-select :forFilters="true" :selectedValues="slotProps.filters['inspection_observations.jobsite_id']" v-on:vselected="((data) => { slotProps.filters['inspection_observations.jobsite_id'] = data || {};  })" />
                                    </div>
                                </div>
                                <hr />
                                <div>
                                    <div class="d-inline-block" style="min-width: 250px;">
                                        <user-select
                                            :forFilters="true"
                                            :multiple="true"
                                            :selectedValues="slotProps.filters['inspection_observations.user_id']"
                                            v-on:vuselected="((data) => { slotProps.filters['inspection_observations.user_id'] = data || [];  })"
                                            placeholder="Creators"
                                            :appendCurrentUserOption="true" />
                                    </div>
                                </div>
                            </template>
                        </table-filters-2>
                    </div>

                    <button type="button" class="btn btn-outline-success ml-2" v-on:click="exportDataTable()" :disabled="exportingTable" v-if="showExport">
                        <template v-if="!exportingTable"><i class="fa fa-file-excel-o"></i> Export</template>
                        <template v-else><i class="fa fa-circle-o-notch fa-spin"></i> Exporting</template>
                    </button>

                    <div class="clearfix d-md-none d-block"></div>
                </div>

                <div class="clearfix d-md-none d-block"></div>
            </div>

            <router-link slot="observation_id" slot-scope="props" :to="`/observations/view/${props.row.observation_id}`" title="View Observation" class="btn btn-sm btn-light">
                View
            </router-link>

            <template slot="observation_date" slot-scope="props">
                <span v-if="props.row.observation_date">{{ props.row.observation_date | moment("MM/DD/YYYY") }}</span>
            </template>

            <template slot="compliance_status_name" slot-scope="props">
                <label :class="'badge badge-'+(props.row.compliance_status_id === 1 ? 'success' : 'danger')">{{ props.row.compliance_status_name }}</label>
            </template>

            <template slot="status_name" slot-scope="props">
                <label :class="`badge badge-${props.row.status_color}`">{{ props.row.status_name }}</label>
            </template>

            <template slot="ElementText" slot-scope="props">
                {{ props.row.SectionOrder }}.{{ props.row.ElementOrder }} {{ props.row.ElementText }}
            </template>

            <template slot="LastName" slot-scope="props">
                <user-name :user="props.row" />
            </template>

            <template slot="assignee_first_name" slot-scope="props">
                <template v-if="props.row.assignee_subcontractor_id">
                    <label class="badge badge-warning small" title="User is a Subcontractor">SUB</label>
                </template>
                <user-name :user="assignee(props.row)" />
            </template>

            <router-link slot="JobsiteName" slot-scope="props" :to="`/jobsites/view/${props.row.jobsite_id}`" title="View Jobsite">
                {{ props.row.JobsiteNumber }} - {{ props.row.JobsiteName }}
            </router-link>

            <template slot="DivisionName" slot-scope="props">
                {{ props.row.division_code }} - {{ props.row.DivisionName }}
            </template>

            <template slot="subcontractor_name" slot-scope="props">
                <div v-if="props.row.subcontractor_id">
                    <router-link :to="`/subcontractors/view/${props.row.subcontractor_id}`" :title="`View Subcontractor`" v-if="$root.hasPermission('subcontractors.view')">
                        {{ props.row.subcontractor_name }}
                    </router-link>
                    <span v-else-if="props.row.subcontractor">
                        {{ props.row.subcontractor }}
                    </span>
                </div>
            </template>

            <template slot="completed_at" slot-scope="props">
                <template v-if="props.row.completed_at">{{ props.row.completed_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}</template>
            </template>

            <template slot="child_row" slot-scope="props">
                <span class="font-weight-bold">Details:</span><br>
                <div v-html="props.row.detail"></div>
            </template>
        </v-server-table>
    </div>
</template>

<script>
    import filterExtractor from '@/helpers/TableFilterExtractor';
    import customVTColDropdown from '@/components/vue-tables-2/CustomColumnDropdown';

    export default {
        props: {
            assetType: {
                type: String,
                default: null,
            },

            assetId: {
                type: Number,
                default: null,
            },

            showExport: {
                type: Boolean,
                default: true,
            },

            allowFilterSaving: {
                type: Boolean,
                default: false,
            },

            filterId: {
                type: Number,
                default: null,
            },

            dashboardFilters: {
                type: Object,
                default(){
                    return null;
                },
            },

            tableConfig: {
                type: Object,
                default(){
                    return null;
                },
            },
        },

        data: function() {
            return {
                loading: true,
                exportingTable: false,
                inspection_form_id: null,
                forms: {},
                userTimezone: this.$store.state.user.timezone,
                userFilterId: {},
                tableName: "obsTable",
                columns: [
                    'observation_id',
                    'observation_date',
                    'compliance_status_name',
                    'status_name',
                    'form_name',
                    'ElementText',
                    'LastName',
                    'assignee_first_name',
                    'JobsiteName',
                    'DivisionName',
                    'subcontractor_name',
                    'completed_at',
                    'hours_to_completion',
                ],

                filterKey: 'observationFilters',

                filterDefs: {
                    'jobsite.DivisionId': [],
                    'inspection_observations.jobsite_id' : null,
                    'inspection_observations.observation_date' : '',
                    'inspectionelement.InspectionSectionId': [],
                    'inspectionelement.InspectionElementId': [],
                    'corrective_actions.status_id': [],
                    'inspection_observations.compliance_status_id': [],
                    'inspection_observations.subcontractor_id': null,
                    'inspection_observations.user_id': [],
                }
            }
        },

        watch: {
            //Watch the Inspection Form ID
            inspection_form_id(newVal, oldValu){
                //Make sure loading is done
                if(!this.$refs.obsTable || !this.$refs.obsTable.$slots.beforeFilter)
                {
                    return;
                }

                //Reset the Filters?
                this.$refs.obsTable.$slots.beforeFilter[0].context.$refs.obsFilters.setUrlFilters({'inspection_form_id': this.inspection_form_id});
                this.$refs.obsTable.$slots.beforeFilter[0].context.$refs.obsFilters.reset();

                //Re-Fetch the Data - The RESET Above trigger a getData
                //this.getData();
            },

            appliedFilter(newVal, oldVal){
                //Call the helper
                this.checkForAppliedFilter();
            },
        },

        computed: {
            filters(){
                //Check State
                if(this.$store.state.tableFilters.filters[this.filterKey])
                {
                    //Send it
                    return this.$store.state.tableFilters.filters[this.filterKey];
                }

                //Return Definition
                return JSON.parse(JSON.stringify(this.filterDefs));
            },

            appliedFilter(){
                //Send it
                return this.$store.state.appliedFilters[this.filterKey] ? this.$store.state.appliedFilters[this.filterKey] : null;
            },

            options(){
                return {
                    headings: {
                        observation_id: 'Actions',
                        observation_date: 'Date',
                        compliance_status_name: 'Compliance',
                        status_name: 'Status',
                        form_name: 'Form',
                        ElementText: 'Item',
                        LastName: 'Creator',
                        assignee_first_name: 'Assignee',
                        JobsiteName: 'Jobsite',
                        DivisionName: 'Division',
                        subcontractor_name: 'Subcontractor',
                        completed_at: 'Completed',
                        hours_to_completion: 'Hours to Completion',
                    },
                    saveState: this.allowFilterSaving,
                    columnsDropdown: customVTColDropdown,
                    visibleColumns: (this.allowFilterSaving ? this.$root.getVisibleColumnsFromState(this.tableName) : false),
                    sendInitialRequest: (this.filterId ? false : true),
                    orderBy: {
                        ascending: false,
                        column: 'observation_date',
                    },
                    responseAdapter: (response) => {
                        //Emit the count
                        this.$emit('obsvListCount', response.data.count);

                        //Send it
                        return response.data;
                    },
                    requestAdapter: (data) => {
                        //Set the filters
                        data.filters = (this.dashboardFilters ? filterExtractor.extractFilters(this.dashboardFilters) : filterExtractor.extractFilters(this.filters));

                        //INclude the asset
                        data.assetType = this.assetType;
                        data.assetId = this.assetId;

                        //Send it
                        return data;
                    },
                    uniqueKey: 'observation_id',
                };
            },
        },

        methods: {
            assignee(data) {
                return {
                    FirstName: data.assignee_first_name,
                    LastName: data.assignee_last_name,
                    deleted_at: data.assignee_deleted_at,
                };
            },

            exportDataTable(){
                //Loading indicator
                this.exportingTable = true;

                //Build the payload
                var payload = {
                    filters: filterExtractor.extractFilters(this.filters),
                    query: (this.$refs.obsTable.getRequestParams()).query,
                    orderBy: this.$refs.obsTable.options.orderBy.column,
                    ascending: this.$refs.obsTable.options.orderBy.ascending,
                    table: 'obsTable',
                    columns: this.$refs.obsTable.columns,
                    visible_columns: this.$root.getVisibleColumnsFromState(this.tableName),
                };

                //Send it
                this.$http.get('/observations/list?export=true', payload)
                    .then(response => {
                        //Open the new window
                        window.open(response.data);
                    })
                    .catch(err => {
                        //Alert the user
                        this.$toastr.error(err.response.data, 'Failed to Export Observation List');
                    })
                    .then(response => {
                        //reset
                        this.exportingTable = false;
                    });
            },

            getForms(){
                //Send it
                this.$http
                    .get('reports/inspection-forms')
                    .then(response => {
                        //Done lOading
                        this.loading = false;

                        //Setter
                        this.inspection_form_id = response.data.default_form_id;
                        this.forms = response.data.forms;
                    })
                    .catch(err => {
                        //Alert
                        this.$toastr.error(err.response);
                    })
                    .then(() => {
                        //Done lOading
                        this.loading = false;
                    });
            },

            refreshTableData(filters) {
                //Set the data
                this.$refs.obsTable.setPage(1);
                //this.$refs.obsTable.getData();
            },

            filtersCleared(){
                //Reset
                this.refreshTableData();

                //Reset the Columns
                this.$refs.obsFilters.resetColVis(this.$refs.obsTable);
            },

            setUserFilterData(){
                //Call the Helper
                this.$refs.obsSavedFilters.setFilterParams(this.$refs.obsTable);
            },

            checkForAppliedFilter(){
                //Check if
                if(!this.allowFilterSaving || !this.appliedFilter)
                {
                    return;
                }

                //Call the helper
                this.$refs.obsFilters.setColVis(this.$refs.obsTable);
            },
        },

        mounted() {
            //Get the forms
            this.getForms();

            //Check for Staring Filters
            if(!this.dashboardFilters)
            {
                //Set it
                //this.$refs.incTableFilters.setFilters(this.dashboardFilters);

                //Check for a saved filter
                this.checkForAppliedFilter();

                //Check for Filter ID
                if(this.filterId)
                {
                    this.$refs.obsSavedFilters.setSavedFilter(this.filterId);
                }
            }

            //Check for Table Config
            if(this.tableConfig)
            {
                //Set it
                this.$refs.obsFilters.setColVisFromSavedFilter(this.$refs.obsTable, this.tableConfig);
            }
        }
    }
</script>
